import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { LOGO_URL } from "../lib/data";

/**
 * Generic legal / policy page layout — Apple-inspired:
 *  - Crisp typography hierarchy
 *  - Lots of vertical whitespace
 *  - Subtle red hairline + LED accent on top
 *  - Fixed width reading column
 */
export const LegalLayout = ({ eyebrow, title, updated, children }) => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <main className="relative bg-[#050505] text-white min-h-screen grain">
      {/* Minimal top nav */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/85 backdrop-blur-xl border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="legal-brand-logo">
            <img
              src={LOGO_URL}
              alt="STRAßENWERT"
              className="h-10 md:h-11 w-auto drop-shadow-[0_0_14px_rgba(255,0,51,0.35)] group-hover:drop-shadow-[0_0_22px_rgba(255,0,51,0.6)] transition-all duration-500"
            />
          </Link>
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-[11px] font-semibold tracking-[0.25em] uppercase text-zinc-400 hover:text-white border border-white/15 px-4 py-2.5 hover:border-[#ff0033]/60 hover:shadow-[0_0_20px_rgba(255,0,51,0.25)] transition-all"
          >
            <ArrowLeft size={14} /> Zurück
          </Link>
        </div>
      </header>

      {/* Hero band */}
      <section className="relative pt-40 pb-16 md:pt-48 md:pb-20 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px hairline-red opacity-60" />
        <div className="absolute -top-20 -right-20 w-[30rem] h-[30rem] rounded-full bg-[#ff0033]/15 blur-[140px] pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.4em] uppercase text-zinc-500 font-semibold">
            <span className="red-led" />
            {eyebrow}
          </div>
          <h1 className="mt-8 font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-7xl">
            {title}
          </h1>
          {updated && (
            <p className="mt-6 text-[10px] tracking-[0.3em] uppercase text-zinc-600">
              Stand · {updated}
            </p>
          )}
        </div>
      </section>

      {/* Body */}
      <section className="relative pb-28 md:pb-40">
        <div className="max-w-3xl mx-auto px-6 md:px-10 legal-body">
          {children}
        </div>
      </section>

      <LegalFooter />

      <style>{`
        .legal-body {
          font-family: 'Manrope', sans-serif;
          font-size: 16px;
          line-height: 1.75;
          color: #a1a1aa;
        }
        .legal-body h2 {
          font-family: 'Cabinet Grotesk', sans-serif;
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.01em;
          text-transform: uppercase;
          color: #fff;
          margin: 56px 0 14px 0;
          padding-bottom: 14px;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .legal-body h3 {
          font-family: 'Cabinet Grotesk', sans-serif;
          font-size: 15px;
          font-weight: 700;
          letter-spacing: 0.15em;
          text-transform: uppercase;
          color: #fff;
          margin: 32px 0 10px 0;
        }
        .legal-body p { margin: 0 0 18px 0; }
        .legal-body a { color: #fff; text-decoration: underline; text-decoration-color: rgba(255,0,51,0.6); text-underline-offset: 4px; }
        .legal-body a:hover { text-decoration-color: #ff0033; }
        .legal-body ul { list-style: none; padding: 0; margin: 0 0 20px 0; }
        .legal-body li { position: relative; padding-left: 18px; margin-bottom: 8px; }
        .legal-body li::before {
          content: '';
          position: absolute;
          left: 0; top: 10px;
          width: 6px; height: 6px;
          background: #ff0033;
          box-shadow: 0 0 8px #ff0033;
        }
        .legal-body strong { color: #fff; font-weight: 700; }
        .legal-body hr {
          border: 0;
          border-top: 1px solid rgba(255,255,255,0.06);
          margin: 40px 0;
        }
      `}</style>
    </main>
  );
};

const LegalFooter = () => (
  <div className="border-t border-white/[0.06] bg-[#030303] py-10">
    <div className="max-w-4xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-between gap-5 text-[10px] tracking-[0.3em] uppercase text-zinc-600">
      <div className="flex items-center gap-5">
        <Link to="/impressum" className="hover:text-white transition-colors" data-testid="legal-link-impressum">
          Impressum
        </Link>
        <Link to="/datenschutz" className="hover:text-white transition-colors" data-testid="legal-link-datenschutz">
          Datenschutz
        </Link>
        <Link to="/agb" className="hover:text-white transition-colors" data-testid="legal-link-agb">
          AGB
        </Link>
      </div>
      <div className="flex items-center gap-5">
        <a
          href="mailto:info@strassenwert.de"
          className="flex items-center gap-2 hover:text-white transition-colors"
        >
          <Mail size={12} className="text-[#ff0033]" /> info@strassenwert.de
        </a>
        <a
          href="tel:+491755003192"
          className="flex items-center gap-2 hover:text-white transition-colors"
        >
          <Phone size={12} className="text-[#ff0033]" /> +49 175 50 03 19 2
        </a>
      </div>
    </div>
  </div>
);

export default LegalLayout;
